import type { CSSProperties } from 'react';
import { COLORS } from '../../constants/theme';
import type { DragonType } from '../../types/dragon';

/**
 * SF-153: shared, non-component home for Dragons' presentation constants/helpers — both
 * `MobileDragonsShell`/`DesktopDragonsShell` import from here rather than each independently
 * duplicating them (`react-refresh/only-export-components` blocks a value export like
 * `DRAGON_TYPES` from a component file — same `constants/navPillars.ts`/`constants/sidebarNav.ts`
 * precedent). Moved verbatim out of `Dragons.tsx` — literal color values are unchanged (out of
 * scope for this migration, see spec Key Implementation Note 7).
 *
 * Note: `utils/dragonAnnotations.ts` is a second, unrelated, contradictory dragon-type effect
 * catalog (Dossier kingdom header) — do not merge or reconcile it with `DRAGON_TYPES` here.
 */
export const DRAGON_TYPES: { value: DragonType; label: string; effect: string }[] = [
  { value: 'EMERALD', label: 'Emerald', effect: '+15% combat losses to attackers, -15% attack gains' },
  { value: 'SAPPHIRE', label: 'Sapphire', effect: '-30% thievery & magic effectiveness' },
  { value: 'GOLD', label: 'Gold', effect: '-25% building efficiency' },
  { value: 'RUBY', label: 'Ruby', effect: '-8% military efficiency' },
];

export function stateBg(state: string | null): string {
  switch (state) {
    case 'ACTIVE': return '#3a1a1a';
    case 'FUNDING': return '#2a1a00';
    case 'FUNDED_READY': return '#1a2a1a';
    case 'SLAIN': return '#0a1a0a';
    case 'EXPIRED':
    case 'CANCELLED': return '#1a1a1a';
    default: return COLORS.cardAlt;
  }
}

export function stateFg(state: string | null): string {
  switch (state) {
    case 'ACTIVE': return '#ff6644';
    case 'FUNDING': return COLORS.warning;
    case 'FUNDED_READY': return '#66cc66';
    case 'SLAIN': return '#88cc88';
    case 'EXPIRED':
    case 'CANCELLED': return COLORS.text.muted;
    default: return COLORS.text.tertiary;
  }
}

export const inputStyle: CSSProperties = {
  marginTop: 4,
  padding: '6px 8px',
  background: COLORS.cardAlt,
  border: `1px solid ${COLORS.borderInset}`,
  borderRadius: 4,
  color: COLORS.text.primary,
  fontSize: 13,
  width: '100%',
  boxSizing: 'border-box',
};

export function primaryButtonStyle(disabled: boolean): CSSProperties {
  return {
    padding: '8px 14px', marginTop: 10,
    background: disabled ? COLORS.cardAlt : COLORS.gold,
    color: disabled ? COLORS.text.muted : '#1a1406',
    border: `1px solid ${disabled ? COLORS.border : COLORS.gold}`,
    borderRadius: 4, fontSize: 13, fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

export function secondaryButtonStyle(disabled: boolean): CSSProperties {
  return {
    padding: '8px 14px',
    background: COLORS.cardAlt,
    color: disabled ? COLORS.text.muted : COLORS.text.secondary,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 4, fontSize: 13,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

export function dangerButtonStyle(disabled: boolean): CSSProperties {
  return {
    padding: '8px 14px',
    background: disabled ? COLORS.cardAlt : '#4a2020',
    color: disabled ? COLORS.text.muted : '#ff8866',
    border: `1px solid ${disabled ? COLORS.border : '#6a3030'}`,
    borderRadius: 4, fontSize: 13,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}
